import type { Infer } from 'superstruct';
import {
	any,
	array,
	boolean,
	number,
	object,
	optional,
	string,
} from 'superstruct';

const FacebookAccount = object({
	account_id: string(),
	id: string(),
});

const FacebookSentenceLine = object({
	content: string(),
	children: array(string()),
});

const FacebookPermissionForActions = object({
	can_edit: boolean(),
	can_see_insight: boolean(),
	can_share: boolean(),
	subtype_supports_lookalike: boolean(),
	supports_recipient_lookalike: boolean(),
});

export const FacebookSavedAudience = object({
	targeting: any(),
	name: string(),
	id: string(),
	account: FacebookAccount,
	approximate_count_lower_bound: number(),
	approximate_count_upper_bound: number(),
	run_status: string(),
	time_created: string(),
	sentence_lines: array(FacebookSentenceLine),
	permission_for_actions: FacebookPermissionForActions,
	time_updated: string(),
});

export type FacebookSavedAudience = Infer<typeof FacebookSavedAudience>;

// Facebook saved audience targeting.geo_locations
const FacebookGeoLocationCity = object({
	country: string(),
	distance_unit: string(),
	key: string(),
	name: string(),
	radius: number(),
	region: string(),
	region_id: string(),
});

const FacebookGeoLocationZip = object({
	key: string(),
	name: string(),
	primary_city_id: number(),
	region_id: number(),
	country: string(),
});

const FacebookGeoLocationCustomLocation = object({
	name: string(),
	address_string: string(),
	distance_unit: string(),
	latitude: number(),
	longitude: number(),
	radius: number(),
	primary_city_id: number(),
	region_id: number(),
	country: string(),
});

const FacebookGeoLocationPlace = object({
	key: string(),
	name: string(),
	distance_unit: string(),
	latitude: number(),
	longitude: number(),
	radius: number(),
	primary_city_id: number(),
	region_id: number(),
	country: string(),
});

const FacebookGeoLocationSubcity = object({
	key: string(),
	country: string(),
	name: string(),
	region: string(),
	region_id: string(),
});

export const FacebookSavedAudienceTargetingGeoLocations = object({
	countries: optional(array(string())),
	cities: optional(array(FacebookGeoLocationCity)),
	zips: optional(array(FacebookGeoLocationZip)),
	custom_locations: optional(array(FacebookGeoLocationCustomLocation)),
	places: optional(array(FacebookGeoLocationPlace)),
	location_types: optional(array(string())),
	subcities: optional(array(FacebookGeoLocationSubcity)),
});

export type FacebookSavedAudienceTargetingGeoLocations = Infer<
	typeof FacebookSavedAudienceTargetingGeoLocations
>;

// Facebook saved audience targeting shape including both geo_locations and excluded_geo_locations
export const FacebookSavedAudienceTargeting = object({
	age_max: optional(number()),
	age_min: optional(number()),
	geo_locations: optional(FacebookSavedAudienceTargetingGeoLocations),
	excluded_geo_locations: optional(FacebookSavedAudienceTargetingGeoLocations),
});

export type FacebookSavedAudienceTargeting = Infer<
	typeof FacebookSavedAudienceTargeting
>;
